import React, { useCallback, useState } from 'react';
import { AxiosError } from 'axios';
import { Blockquote, List, ListItem, Modal, Toggle } from '@wfp/ui';

import { WarningEvent, type WarningEventComment } from '../../../restapi/types';
import { useMutation, useQueryClient } from 'react-query';
import { saveComment } from '../../../restapi/warningEvent';
import { extractErrorMessages } from '../../../restapi/restapi';
import { CommentsStatus, commentsStatusLabel, messageWithHeader } from './types';
import { RichTextEditor } from '../RichTextEditor';


interface Props {
	event: WarningEvent;
	onClose: (comment?: WarningEventComment) => void;
}

function prepareInitialMessage(comment: WarningEventComment | null) {
	return (comment?.message) ? '<p></p><p></p>' + messageWithHeader(comment) : '';
}

export function EditComment({event, onClose}: Props) {
	const queryClient = useQueryClient();

	const [isSaving, setIsSaving] = useState(false);
	const [message, setMessage] = useState(prepareInitialMessage(event.comment));
	const [messageChanged, setMessageChanged] = useState(false);
	const [isOpen, setIsOpen] = useState(event.comment?.isOpen !== false);  // undefined defaults to true
	const [errors, setErrors] = useState<string[]>([]);
	const saveMutation = useMutation(() =>
		saveComment(event.comment?.id || 0, event.id, message, isOpen), {
		onMutate: () => setIsSaving(true),
		onSuccess: (comment) => {
			queryClient.invalidateQueries('event');
			queryClient.invalidateQueries('calendar');
			setTimeout(() => onClose(comment));
		},
		onError: (error: AxiosError) => {
			const errors = extractErrorMessages(error);
			if (errors.length) {
				setErrors(errors);
			}
		},
		onSettled: () => setIsSaving(false),
	});

	const validateAndSave = useCallback(
		() => {
			const errors: string[] = [];
			if (!messageChanged || !message) {
				errors.push('Please write a comment before saving');
			}
			if (errors.length) {
				setErrors(errors);
			} else {
				setErrors([]);
				saveMutation.mutate();
			}
		}, [messageChanged, message, saveMutation],
	);

	const disabled = isSaving;

	return (
		<Modal
			open wide
			modalHeading="Comment"
			primaryButtonText="Save"
			secondaryButtonText="Cancel"
			primaryButtonDisabled={disabled}
			secondaryButtonDisabled={disabled}
			onRequestSubmit={() => validateAndSave()}
			onRequestClose={() => onClose()}>

			<RichTextEditor
				disabled={disabled}
				value={message}
				onChange={text => {
					setMessage(text);
					setMessageChanged(true);
				}}
			/>

			<Toggle
				id="close-comment-toggle"
				disabled={disabled}
				toggled={!isOpen}
				labelA={commentsStatusLabel[CommentsStatus.open]}
				labelB={commentsStatusLabel[CommentsStatus.done]}
				onToggle={() => setIsOpen(!isOpen)}
			/>

			{errors.length ?
				<div className="mt2">
					<Blockquote kind="error">
						<List kind="unordered">{errors.map((e, i) =>
							<ListItem key={i}>{e}</ListItem>,
						)}</List>
					</Blockquote>
				</div> : null}
		</Modal>
	);
}